import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import './AdminContentDashboard.css';
import { useApp } from '../../context/AppContext';
import { mediaAPI, profileAPI, postsAPI, adminAPI } from '../../services/api';
import type { AttributionSnapshot } from '../../services/api';
import { usePendingOffers, useAdminStats, useMessages, useOrders } from '../../hooks';
import { toast } from '../../components/Toast/Toast';
import OfferTimer from '../../components/OfferTimer/OfferTimer';
import AddProductModal from '../../components/AddProductModal/AddProductModal';
import RealtimeChatPanel from '../../components/RealtimeChatPanel/RealtimeChatPanel';
import SubscriberPreviewModal, { SubscriberPreview } from '../../components/SubscriberPreviewModal/SubscriberPreviewModal';
import StoryTheater from '../../components/StoryTheater/StoryTheater';
import VideoPreview from '../../components/VideoPreview/VideoPreview';
import OrderRow from '../../components/OrderRow/OrderRow';
import OrderDetailModal from '../../components/OrderDetailModal/OrderDetailModal';
import PushEmailModal, { PushEmailRecipient } from '../../components/PushEmailModal/PushEmailModal';
import StoryViewersModal from '../../components/StoryViewersModal/StoryViewersModal';
import RedditPanel from './RedditPanel';
import type { Order, OrderStatus } from '../../services/api';
import type { Product } from '../../context/AppContext';
import { extractVideoPoster } from '../../utils/extractVideoPoster';
import { isOnline, lastSeenLabel } from '../../utils/presence';
import { decodeHtmlEntities } from '../../utils/decodeHtmlEntities';

type ActiveSection = 'overview' | 'posts' | 'stories' | 'shop' | 'offers' | 'products' | 'orders' | 'messages' | 'subscribers' | 'reddit';
type ShopTab = 'products' | 'orders' | 'offers';

type OrderFilter = 'all' | 'paid' | 'shipped' | 'delivered' | 'refunded';

// Unified inbox conversation type
interface InboxConversation {
  partnerId: string;
  partnerName: string;
  partnerAvatar: string;
  lastMessage: {
    id: string;
    content: string;
    timestamp: Date;
    fromUserId: string;
    toUserId: string;
    read: boolean;
  };
  unreadCount: number;
  /** Partner's last_seen ISO timestamp (from API). Used to render the
   *  green online dot + "last seen X ago" label via utils/presence. */
  partnerLastSeen?: string | null;
  /** Server-derived presence flag. Prefer this over deriving from
   *  partnerLastSeen since server uses authoritative DB time. */
  partnerIsOnline?: boolean;
}

const AdminContentDashboard: React.FC = () => {
  const {
    posts,
    products,
    orders,
    messages,
    adminUser,
    currentUser,
    createPost,
    deletePost,
    deleteProduct,
    getAllPendingOffers,
    acceptOffer,
    declineOffer,
    counterOffer,
    sendMessage,
    updateProfile,
    signOut,
    viewMode,
    setViewMode,
    setIsAdminView
  } = useApp();

  const [sidebarOpen, setSidebarOpen] = useState(() =>
    typeof window === 'undefined' ? true : window.innerWidth >= 900
  );
  const [activeSection, setActiveSection] = useState<ActiveSection>('overview');
  // Shop sub-tab — the consolidated Shop section (Products / Orders /
  // Offers) uses this to remember which tab the admin last picked, so
  // navigating away from Shop and back doesn't lose context. Legacy
  // setActiveSection('orders'|'products'|'offers') calls from widgets
  // remain valid and route to the corresponding tab.
  const [shopTab, setShopTab] = useState<ShopTab>('products');
  const [isCreating, setIsCreating] = useState(false);
  const [createType, setCreateType] = useState<'post' | 'story'>('post');
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [viewingPost, setViewingPost] = useState<typeof posts[0] | null>(null);
  const [viewingStory, setViewingStory] = useState<typeof posts[0] | null>(null);
  // Story viewer-count cache: { [storyId]: viewerCount } — populated when
  // admin opens the Stories section; surfaces on each tile as a small badge.
  const [storyViewerCounts, setStoryViewerCounts] = useState<Record<string, number>>({});
  // Which story's viewer-list modal is open (admin tap on tile badge).
  const [viewersForStory, setViewersForStory] = useState<typeof posts[0] | null>(null);
  const [acceptingOffer, setAcceptingOffer] = useState<{ productId: string; offerId: string; offer: any; product: any } | null>(null);

  // Post creation state
  const [content, setContent] = useState('');
  const [mediaUrl, setMediaUrl] = useState('');
  const [posterUrl, setPosterUrl] = useState('');
  const [mediaType, setMediaType] = useState<'image' | 'video' | 'text'>('text');
  const [isLocked, setIsLocked] = useState(false);
  const [price, setPrice] = useState('');
  const [uploading, setUploading] = useState(false);

  // Subscribers state
  const [subscribers, setSubscribers] = useState<any[]>([]);
  const [subscribersLoaded, setSubscribersLoaded] = useState(false);
  const [subscribersLoading, setSubscribersLoading] = useState(false);

  // Signup-source attribution (last 30 days). Loaded on overview mount.
  const [attribution, setAttribution] = useState<AttributionSnapshot | null>(null);
  useEffect(() => {
    if (activeSection !== 'overview') return;
    if (attribution) return; // first-load only; refresh on remount/section change
    let cancelled = false;
    adminAPI.getAttribution(30)
      .then(snap => { if (!cancelled) setAttribution(snap); })
      .catch(() => { /* silent — card just hides */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);
  const [subscriberFilter, setSubscriberFilter] = useState<'all' | 'active' | 'pending' | 'cancelled'>('all');
  const [subscriberQuery, setSubscriberQuery] = useState('');
  // W6.1: bulk-action selection state (user-ids of selected subs)
  const [selectedSubIds, setSelectedSubIds] = useState<Set<string>>(new Set());
  const [bulkActionRunning, setBulkActionRunning] = useState(false);
  // W7.5: comment moderation queue
  const [moderationOpen, setModerationOpen] = useState(false);
  const [moderationComments, setModerationComments] = useState<any[]>([]);
  const [moderationLoading, setModerationLoading] = useState(false);
  // W6.2: secondary filters (tier + last-seen). Default 'all' means
  // no filter; user combines them with the status tabs and search.
  const [subscriberTierFilter, setSubscriberTierFilter] = useState<'all' | 'monthly' | 'quarterly' | 'yearly' | 'lifetime'>('all');
  const [subscriberSeenFilter, setSubscriberSeenFilter] = useState<'all' | 'online' | 'today' | 'week' | 'month' | 'never'>('all');
  const [previewSub, setPreviewSub] = useState<SubscriberPreview | null>(null);

  // Avatar upload state
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  // Track media URLs that have failed to load. Use React state instead
  // of DOM mutation (img.style.display='none') because React re-renders
  // reset inline styles, which caused the broken-image X to flash back
  // into the post grid after each parent state change.
  const [brokenMedia, setBrokenMedia] = useState<Record<string, boolean>>({});
  const markBroken = (url: string | undefined) => {
    if (!url) return;
    setBrokenMedia(prev => prev[url] ? prev : { ...prev, [url]: true });
  };

  // Messaging state
  const [selectedConversation, setSelectedConversation] = useState<string | null>(null);
  const [conversationQuery, setConversationQuery] = useState('');
  const [conversationFilter, setConversationFilter] = useState<'all' | 'unread'>('all');

  // Offer state
  const [counterAmounts, setCounterAmounts] = useState<Record<string, string>>({});

  // Real API hooks for offers
  const {
    acceptOffer: apiAcceptOffer,
    declineOffer: apiDeclineOffer,
    counterOffer: apiCounterOffer,
    isSubmitting: offerSubmitting
  } = usePendingOffers();

  // Admin stats from API
  const { stats: apiStats } = useAdminStats();

  // Real API conversations
  const {
    conversations: apiConversations,
    loading: messagesLoading,
    markConversationRead,
    refresh: refreshConversations,
  } = useMessages();

  // Orders panel state — admin-only consumer of /api/orders
  const {
    orders: apiOrders,
    loading: ordersLoading,
    refresh: refreshOrders,
    updateStatus: updateOrderStatus,
  } = useOrders();
  const [orderFilter, setOrderFilter] = useState<OrderFilter>('all');
  const [viewingOrder, setViewingOrder] = useState<Order | null>(null);

  // Edit-product modal opens with this product pre-loaded.
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);

  // Admin push-email composer recipient. null = closed.
  const [pushEmailRecipient, setPushEmailRecipient] = useState<PushEmailRecipient | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Computed data
  const regularPosts = posts.filter(p => !p.isStory).sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
  const stories = posts.filter(p => p.isStory).sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
  const pendingOffers = getAllPendingOffers();

  // Pending shipments = paid orders that haven't been shipped yet.
  const pendingShipmentCount = apiOrders.filter(o => o.status === 'paid' || o.status === 'processing').length;
  const filteredOrders = orderFilter === 'all'
    ? apiOrders
    : apiOrders.filter(o => o.status === orderFilter || (orderFilter === 'paid' && o.status === 'processing'));

  // Transform API conversations to inbox format, fallback to mock data if API empty
  const inboxConversations = useMemo((): InboxConversation[] => {
    // Use API conversations if available
    if (apiConversations.length > 0) {
      return apiConversations.map(conv => {
        const c = conv as any; // Handle both old and new API response formats
        const partnerId = c.partner_id || c.subscriber_id || 'unknown';
        const lastMessageContent = decodeHtmlEntities(c.last_message || c.last_message_preview || '');
        // The backend exposes `last_message_sender_id` — use it so the
        // preview can correctly show "You: ..." vs "{partnerName}: ..."
        // instead of always attributing the last message to the partner.
        const lastSenderId = c.last_message_sender_id || c.last_sender_id || null;
        const adminIsLastSender = lastSenderId === 'admin' || lastSenderId === adminUser.id || lastSenderId === currentUser.id;
        return {
          partnerId,
          partnerName: c.partner_name || `User ${partnerId.slice(0, 6)}`,
          partnerAvatar: c.partner_avatar || '',
          lastMessage: {
            id: 'last',
            content: lastMessageContent,
            timestamp: new Date(c.last_message_at || Date.now()),
            // Real sender: admin's id when they sent last, otherwise partner.
            fromUserId: adminIsLastSender ? adminUser.id : partnerId,
            toUserId: adminIsLastSender ? partnerId : currentUser.id,
            read: (c.unread_count || 0) === 0
          },
          unreadCount: c.unread_count || 0,
          partnerLastSeen: c.partner_last_seen || null,
          partnerIsOnline: !!(c.partner_is_online),
        };
      });
    }

    // Fallback to mock messages from context
    const conversationMap = new Map<string, InboxConversation>();

    messages.forEach(msg => {
      // Determine conversation partner
      const isFromAdmin = msg.fromUserId === adminUser.id || msg.fromUserId === currentUser.id;
      const partnerId = isFromAdmin ? msg.toUserId : msg.fromUserId;

      const existing = conversationMap.get(partnerId);
      if (existing) {
        if (!msg.read && !isFromAdmin) existing.unreadCount++;
        if (msg.timestamp > existing.lastMessage.timestamp) {
          existing.lastMessage = {
            id: msg.id,
            content: msg.content,
            timestamp: msg.timestamp,
            fromUserId: msg.fromUserId,
            toUserId: msg.toUserId,
            read: msg.read
          };
        }
      } else {
        conversationMap.set(partnerId, {
          partnerId,
          partnerName: partnerId === 'buyer1' ? 'Demo User' : `User ${(partnerId || '').slice(0, 6)}`,
          partnerAvatar: '',
          lastMessage: {
            id: msg.id,
            content: msg.content,
            timestamp: msg.timestamp,
            fromUserId: msg.fromUserId,
            toUserId: msg.toUserId,
            read: msg.read
          },
          unreadCount: (!msg.read && !isFromAdmin) ? 1 : 0
        });
      }
    });

    return Array.from(conversationMap.values())
      .sort((a, b) => b.lastMessage.timestamp.getTime() - a.lastMessage.timestamp.getTime());
  }, [apiConversations, messages, adminUser.id, currentUser.id]);

  const totalUnreadMessages = inboxConversations.reduce((sum, c) => sum + c.unreadCount, 0);

  // Real metrics - prefer API stats, fallback to local calculations
  const realMetrics = useMemo(() => {
    // Calculate local totals from posts array
    const totalLikes = posts.reduce((sum, p) => sum + p.likes, 0);
    const totalTips = posts.reduce((sum, p) => sum + (p.tips || 0), 0);
    const actualProductsSold = products.reduce((sum, p) => sum + p.sold, 0);

    // Merge API stats with local data
    return {
      // Revenue - from API
      totalRevenue: (apiStats?.total_revenue || 0) + (apiStats?.subscription_revenue || 0),
      subscriptionRevenue: apiStats?.subscription_revenue || 0,
      productRevenue: apiStats?.total_revenue || 0,
      availableBalance: (apiStats as any)?.available_balance || 0,
      pendingBalance: (apiStats as any)?.pending_balance || 0,

      // Counts - prefer API
      subscribers: apiStats?.active_subscribers || 0,
      activeSubscribers: apiStats?.active_subscribers || 0,
      pendingSubscriptions: apiStats?.pending_subscriptions || 0,
      totalPosts: apiStats?.total_posts || regularPosts.length,
      totalStories: apiStats?.total_stories || stories.length,
      activeProducts: apiStats?.active_products || products.filter(p => p.stock > 0).length,
      soldProducts: apiStats?.sold_products || actualProductsSold,

      // Activity - from API
      unreadMessages: apiStats?.unread_messages || totalUnreadMessages,
      pendingOffers: apiStats?.pending_offers || pendingOffers.length,
      awaitingPayment: apiStats?.awaiting_payment || 0,

      // 24h activity
      newUsers24h: apiStats?.new_users_24h || 0,
      newOffers24h: apiStats?.new_offers_24h || 0,
      newMessages24h: apiStats?.new_messages_24h || 0,
      newSubscriptions24h: apiStats?.new_subscriptions_24h || 0,

      // Local calculations
      totalLikes,
      pendingOrders: orders.filter(o => o.status === 'pending').length,
      productsSold: apiStats?.sold_products || actualProductsSold,
      lowStockItems: products.filter(p => p.stock <= 10).length,
      tipRevenue: totalTips,
      pendingOfferCount: apiStats?.pending_offers || pendingOffers.length,

      // Recent activity feeds from API
      recentOffers: apiStats?.recent_offers || [],
      recentSubscribers: apiStats?.recent_subscribers || []
    };
  }, [orders, products, posts, adminUser, totalUnreadMessages, regularPosts, stories, pendingOffers, apiStats]);

  // Auto-select first conversation when none selected.
  //
  // Desktop only: at ≥821px the inbox-rail and chat-pane render side
  // by side, so auto-selecting the first row gives the admin an
  // immediate "ready to chat" state. On mobile the same behavior is a
  // trap — the chat panel takes the whole screen, and every time the
  // user taps "back" to see the inbox this effect re-fires and re-
  // selects the first row, looping them out of the inbox before they
  // can choose someone else to message.
  useEffect(() => {
    if (activeSection !== 'messages') return;
    if (selectedConversation) return;
    if (inboxConversations.length === 0) return;
    if (typeof window !== 'undefined'
        && window.matchMedia('(max-width: 820px)').matches) {
      return;
    }
    setSelectedConversation(inboxConversations[0].partnerId);
  }, [activeSection, selectedConversation, inboxConversations]);

  // W6.3: download the currently-filtered subscriber list as CSV.
  // Uses RFC 4180 quoting (double up internal quotes, wrap any cell
  // containing comma/quote/newline). No external dep — keeps bundle thin.
  const exportSubscribersCsv = useCallback((rows: any[]) => {
    const cols: Array<[string, (s: any) => any]> = [
      ['display_name',          s => s.display_name || ''],
      ['email',                 s => s.email || ''],
      ['plan_type',             s => (s.plan_type || 'monthly').toLowerCase()],
      ['subscription_status',   s => s.subscription_status || (s.is_subscribed ? 'active' : 'pending')],
      ['joined_at',             s => s.created_at || ''],
      ['paid_at',               s => s.paid_at || ''],
      ['last_seen',             s => s.last_seen || ''],
      ['user_id',               s => s.id || ''],
    ];
    const esc = (v: any) => {
      const str = v == null ? '' : String(v);
      return /[",\n\r]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    };
    const header = cols.map(c => c[0]).join(',');
    const body = rows.map(r => cols.map(c => esc(c[1](r))).join(',')).join('\r\n');
    const csv = `${header}\r\n${body}\r\n`;
    const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `onlyjalen-subscribers-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, []);

  const fetchSubscribers = useCallback(async (filter: 'all' | 'active' | 'pending' | 'cancelled' = 'all') => {
    setSubscribersLoading(true);
    try {
      const token = localStorage.getItem('onlyjalen_access_token');
      const qs = filter !== 'all' ? `?status=${filter}` : '';
      const res = await fetch(`https://onlyjalen-api.jalen1wa.workers.dev/api/admin/subscribers${qs}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json() as any;
      setSubscribers(data.subscribers || []);
      setSubscribersLoaded(true);
    } catch { /* silent */ } finally {
      setSubscribersLoading(false);
    }
  }, []);

  // Fetch subscribers when section opens
  useEffect(() => {
    if (activeSection === 'subscribers') {
      fetchSubscribers(subscriberFilter);
    }
  }, [activeSection, subscriberFilter, fetchSubscribers]);

  // Prefetch viewer counts for the Stories grid. Fires when the admin
  // opens the Stories section, only for stories we don't have counts
  // for yet — so re-opening the section doesn't re-fetch everything.
  useEffect(() => {
    if (activeSection !== 'stories') return;
    const toFetch = stories.filter(s => storyViewerCounts[s.id] === undefined);
    if (toFetch.length === 0) return;
    let cancelled = false;
    (async () => {
      // Parallel — typical story counts are <10 so this is fine.
      const results = await Promise.all(toFetch.map(async s => {
        try {
          const r = await postsAPI.getViews(s.id);
          return [s.id, r?.totalViewers ?? 0] as const;
        } catch {
          return [s.id, 0] as const;
        }
      }));
      if (cancelled) return;
      setStoryViewerCounts(prev => {
        const next = { ...prev };
        for (const [id, n] of results) next[id] = n;
        return next;
      });
    })();
    return () => { cancelled = true; };
    // stories.length is the right dependency — the array identity changes
    // every render, but we only need to refetch when story SET changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection, stories.length]);

  // W7.5: fetch the recent-comments moderation queue.
  const fetchModerationQueue = useCallback(async () => {
    setModerationLoading(true);
    try {
      const token = localStorage.getItem('onlyjalen_access_token');
      const res = await fetch('https://onlyjalen-api.jalen1wa.workers.dev/api/admin/comments?limit=50', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json() as any;
      setModerationComments(data.comments || []);
    } catch {
      toast.error('Could not load moderation queue');
    } finally {
      setModerationLoading(false);
    }
  }, []);

  const deleteCommentById = async (commentId: string) => {
    if (typeof window !== 'undefined' && !window.confirm('Delete this comment? This cannot be undone.')) return;
    try {
      const token = localStorage.getItem('onlyjalen_access_token');
      const res = await fetch(`https://onlyjalen-api.jalen1wa.workers.dev/api/comments/${commentId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setModerationComments(prev => prev.filter(c => c.id !== commentId));
      toast.success('Comment removed');
    } catch (e: any) {
      toast.error(`Delete failed${e?.message ? ` (${e.message})` : ''}`);
    }
  };

  // W6.1: run approve/deny across all currently-selected subscribers
  // sequentially (so a single network blip doesn't cascade into N
  // confusing errors). Returns silently on empty selection.
  const handleBulkSubscriberAction = async (action: 'approve' | 'deny') => {
    if (selectedSubIds.size === 0) return;
    const ids = Array.from(selectedSubIds);
    const confirmLabel = action === 'approve'
      ? `Approve ${ids.length} subscriber${ids.length === 1 ? '' : 's'}?`
      : `Deny ${ids.length} subscriber${ids.length === 1 ? '' : 's'}? This cannot be undone.`;
    if (typeof window !== 'undefined' && !window.confirm(confirmLabel)) return;
    setBulkActionRunning(true);
    const token = localStorage.getItem('onlyjalen_access_token');
    let ok = 0;
    let failed = 0;
    for (const id of ids) {
      try {
        const res = await fetch(`https://onlyjalen-api.jalen1wa.workers.dev/api/admin/subscribers/${id}/${action}`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (res.ok) ok++; else failed++;
      } catch { failed++; }
    }
    setBulkActionRunning(false);
    setSelectedSubIds(new Set());
    await fetchSubscribers(subscriberFilter);
    if (failed === 0) {
      toast.success(`${action === 'approve' ? 'Approved' : 'Denied'} ${ok} subscriber${ok === 1 ? '' : 's'}`);
    } else {
      toast.error(`${ok} succeeded, ${failed} failed`);
    }
  };

  const handleSubscriberAction = async (userId: string, action: 'approve' | 'deny') => {
    const token = localStorage.getItem('onlyjalen_access_token');
    try {
      const res = await fetch(`https://onlyjalen-api.jalen1wa.workers.dev/api/admin/subscribers/${userId}/${action}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      toast.success(action === 'approve' ? 'Subscriber approved' : 'Subscriber denied');
      // Refresh list
      await fetchSubscribers(subscriberFilter);
    } catch (e: any) {
      console.error(`[subscriber ${action}] failed:`, e);
      toast.error(`Could not ${action} subscriber${e?.message ? ` (${e.message})` : ''}`);
    }
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    setUploadingAvatar(true);
    try {
      const result = await mediaAPI.uploadFile(file);
      if (!result.url) throw new Error('No URL returned');
      // Update local state immediately
      updateProfile({ avatar: result.url });
      // Persist to API (correct avatar endpoint)
      await profileAPI.updateAvatar(result.url);
    } catch (err) {
      console.error('Avatar upload failed:', err);
      toast.error('Avatar upload failed');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 100MB limit
    if (file.size > 100 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 100MB.');
      e.target.value = '';
      return;
    }

    setUploading(true);
    setPosterUrl('');
    try {
      const result = await mediaAPI.uploadFile(file);
      if (result.url) {
        setMediaUrl(result.url);
        const isVideo = file.type.startsWith('video/');
        setMediaType(isVideo ? 'video' : 'image');

        // For videos, extract a poster frame client-side and upload it as a
        // sibling asset. Worker stores poster_url alongside media_url so
        // every video tile has a still image even before it streams.
        // Best-effort: if extraction or upload fails, the post still saves.
        if (isVideo) {
          try {
            const blob = await extractVideoPoster(file);
            if (blob) {
              const posterFile = new File(
                [blob],
                file.name.replace(/\.[^.]+$/, '') + '.poster.jpg',
                { type: 'image/jpeg' }
              );
              const posterRes = await mediaAPI.uploadFile(posterFile);
              if (posterRes.url) setPosterUrl(posterRes.url);
            }
          } catch (posterErr) {
            console.warn('Poster extraction skipped:', posterErr);
          }
        }
      }
    } catch (err) {
      console.error('Upload failed:', err);
      toast.error('Upload failed. Please try again.');
      e.target.value = '';
    } finally {
      setUploading(false);
    }
  };

  const handleCreatePost = async () => {
    if (!content.trim() && !mediaUrl) return;

    setUploading(true);
    try {
      const isStory = createType === 'story';
      const expiresAt = isStory
        ? new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
        : undefined;

      // Persist to API first
      await postsAPI.create({
        content: content.trim(),
        mediaType: mediaUrl ? (mediaType as 'image' | 'video' | 'gallery') : undefined,
        mediaUrl: mediaUrl || undefined,
        posterUrl: posterUrl || undefined,
        isLocked,
        price: isLocked && price ? parseFloat(price) : undefined,
        isStory,
        expiresAt,
      });

      // Update local state
      createPost({
        content: content.trim(),
        mediaType: mediaUrl ? mediaType : 'text',
        mediaUrl: mediaUrl || undefined,
        posterUrl: posterUrl || undefined,
        isLocked,
        price: isLocked && price ? parseFloat(price) : undefined,
        isStory,
      });

      setContent('');
      setMediaUrl('');
      setPosterUrl('');
      setMediaType('text');
      setIsLocked(false);
      setPrice('');
      setIsCreating(false);
    } catch (err) {
      console.error('Create failed:', err);
      toast.error('Failed to save post');
    } finally {
      setUploading(false);
    }
  };

  const formatTime = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (minutes < 1) return 'now';
    if (minutes < 60) return `${minutes}m`;
    if (hours < 24) return `${hours}h`;
    return `${days}d`;
  };

  // Per-conversation timestamp formatter used in the inbox.
  // Mirrors the patterns subscribers see in messaging apps:
  //   < 1 min       → "now"
  //   today         → "HH:MM"
  //   yesterday     → "Yesterday"
  //   this week     → "Mon", "Tue", …
  //   this year     → "Mar 12"
  //   earlier       → "Mar 12, 2025"
  // The bucket label below uses the same boundaries for day-grouping.
  const formatConvTime = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    if (diffMs < 60_000) return 'now';
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const startOfYesterday = startOfToday - 86_400_000;
    const ts = date.getTime();
    if (ts >= startOfToday) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    if (ts >= startOfYesterday) return 'Yesterday';
    if (diffMs < 7 * 86_400_000) {
      return date.toLocaleDateString([], { weekday: 'short' });
    }
    if (date.getFullYear() === now.getFullYear()) {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
    return date.toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' });
  };

  // Day-bucket assignment + display label for the section dividers in
  // the conversation list. Returns a stable key so rows in the same
  // bucket render under the same header. Labels match Gmail/iMessage.
  const conversationBucket = (date: Date): { key: 'today' | 'yesterday' | 'week' | 'earlier'; label: string } => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const startOfYesterday = startOfToday - 86_400_000;
    const ts = date.getTime();
    if (ts >= startOfToday) return { key: 'today', label: 'Today' };
    if (ts >= startOfYesterday) return { key: 'yesterday', label: 'Yesterday' };
    if (now.getTime() - ts < 7 * 86_400_000) return { key: 'week', label: 'This Week' };
    return { key: 'earlier', label: 'Earlier' };
  };

  // Mark every unread conversation read at once. The underlying hook
  // does an optimistic local update + best-effort server POST, so
  // unread badges clear instantly and reconcile in the background.
  const markAllConversationsRead = () => {
    inboxConversations
      .filter(c => c.unreadCount > 0)
      .forEach(c => markConversationRead(c.partnerId));
  };

  // Two badge kinds:
  //  - 'count'  → neutral grey pill (e.g. "Posts 20" — just inventory)
  //  - 'unread' → brand-pink pill ("Messages 1", "Offers 3" — needs attention)
  // Visually distinct so the user can scan the nav for what's new without
  // reading numbers.
  const sidebarItems: Array<{
    id: ActiveSection; label: string; icon: string;
    badge?: number; badgeKind?: 'count' | 'unread';
  }> = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'posts', label: 'Posts', icon: '📝', badge: regularPosts.length, badgeKind: 'count' },
    { id: 'stories', label: 'Stories', icon: '📸', badge: stories.length, badgeKind: 'count' },
    // Consolidated Shop entry — combines Products, Orders, and Offers
    // into a single tabbed workspace. The badge shows total pending
    // work (offers awaiting response + orders awaiting ship) so the
    // admin can spot urgent commerce work at a glance.
    {
      id: 'shop',
      label: 'Shop',
      icon: '🛍️',
      badge: (pendingOffers.length + pendingShipmentCount) || undefined,
      badgeKind: (pendingOffers.length + pendingShipmentCount) > 0 ? 'unread' : 'count',
    },
    { id: 'messages', label: 'Messages', icon: '✉️', badge: totalUnreadMessages, badgeKind: 'unread' },
    { id: 'subscribers', label: 'Subscribers', icon: '👥', badge: realMetrics.pendingSubscriptions > 0 ? realMetrics.pendingSubscriptions : undefined, badgeKind: 'unread' },
    { id: 'reddit', label: 'Reddit', icon: '🅡' },
  ];

  return (
    <div className={`admin-dashboard-layout ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
      {sidebarOpen && (
        <div
          className="sidebar-backdrop"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}
      {/* Sidebar */}
      <aside className={`dashboard-sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <button className="hamburger-btn" onClick={() => setSidebarOpen(!sidebarOpen)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              {sidebarOpen ? (
                <path d="M18 6L6 18M6 6l12 12"/>
              ) : (
                <>
                  <line x1="3" y1="6" x2="21" y2="6"/>
                  <line x1="3" y1="12" x2="21" y2="12"/>
                  <line x1="3" y1="18" x2="21" y2="18"/>
                </>
              )}
            </svg>
          </button>
          {sidebarOpen && <span className="sidebar-title">Creator Hub</span>}
          {sidebarOpen && (
            <button
              className="theme-toggle-btn"
              onClick={() => setViewMode(viewMode === 'dark' ? 'light' : 'dark')}
              aria-label="Toggle theme"
              title={viewMode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {viewMode === 'dark' ? (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="4"/>
                  <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/>
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                </svg>
              )}
            </button>
          )}
        </div>

        {/* Profile Section in Sidebar */}
        {sidebarOpen && (
          <div className="sidebar-profile">
            <div className="sidebar-avatar-container">
              <img src={currentUser.avatar || adminUser.avatar} alt="" className="sidebar-avatar" />
              <button
                className="avatar-edit-btn"
                onClick={() => avatarInputRef.current?.click()}
                disabled={uploadingAvatar}
              >
                {uploadingAvatar ? (
                  <span className="mini-spinner"></span>
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/>
                    <circle cx="12" cy="13" r="4"/>
                  </svg>
                )}
              </button>
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/*"
                onChange={handleAvatarUpload}
                style={{ display: 'none' }}
              />
            </div>
            <h3 className="sidebar-username">{currentUser.displayName || adminUser.displayName}</h3>
            <span className="sidebar-role">Creator</span>
          </div>
        )}

        {/* Navigation */}
        <nav className="sidebar-nav">
          {sidebarItems.map(item => (
            <button
              key={item.id}
              className={`sidebar-nav-item ${activeSection === item.id ? 'active' : ''}`}
              onClick={() => {
                setActiveSection(item.id);
                if (typeof window !== 'undefined' && window.innerWidth < 900) {
                  setSidebarOpen(false);
                }
              }}
              title={item.label}
            >
              <span className="nav-icon">{item.icon}</span>
              {sidebarOpen && (
                <>
                  <span className="nav-label">{item.label}</span>
                  {item.badge !== undefined && item.badge > 0 && (
                    <span className={`nav-badge nav-badge-${item.badgeKind || 'count'}`}>{item.badge}</span>
                  )}
                </>
              )}
            </button>
          ))}
        </nav>

        {/* Quick Actions */}
        {sidebarOpen && (
          <div className="sidebar-actions">
            <button className="sidebar-action-btn primary" onClick={() => { setCreateType('post'); setIsCreating(true); }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19"/>
                <line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              New Post
            </button>
            <button className="sidebar-action-btn secondary" onClick={() => { setCreateType('story'); setIsCreating(true); }}>
              Add Story
            </button>
            {setIsAdminView && (
              <button
                className="sidebar-action-btn secondary view-as-user"
                onClick={() => setIsAdminView(false)}
                title="See exactly what your fans see"
              >
                👁  View as Subscriber
              </button>
            )}
          </div>
        )}

        {/* Sign Out */}
        {sidebarOpen && signOut && (
          <div className="sidebar-footer">
            <button className="sidebar-signout-btn" onClick={signOut}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/>
                <polyline points="16 17 21 12 16 7"/>
                <line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
              Sign Out
            </button>
          </div>
        )}
      </aside>

      {/* Main Content */}
      <main className="dashboard-main">
        {/* Mobile Header */}
        <header className="mobile-header">
          <button className="hamburger-btn" onClick={() => setSidebarOpen(!sidebarOpen)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="6" x2="21" y2="6"/>
              <line x1="3" y1="12" x2="21" y2="12"/>
              <line x1="3" y1="18" x2="21" y2="18"/>
            </svg>
          </button>
          <h1>Creator Dashboard</h1>
        </header>

        {/* OVERVIEW SECTION */}
        {activeSection === 'overview' && (
          <div className="dashboard-overview">
            {/* Welcome Banner */}
            <div className="welcome-banner">
              <div className="welcome-text">
                <h1>Performance Overview</h1>
                <p>
                  Welcome back, {currentUser.displayName || adminUser.displayName}.
                  {' '}
                  {realMetrics.newSubscriptions24h > 0 || realMetrics.newMessages24h > 0
                    ? <>You picked up <strong>{realMetrics.newSubscriptions24h}</strong> new {realMetrics.newSubscriptions24h === 1 ? 'subscriber' : 'subscribers'} and <strong>{realMetrics.newMessages24h}</strong> new {realMetrics.newMessages24h === 1 ? 'message' : 'messages'} in the last 24h.</>
                    : <>Here's what's happening in your creator hub.</>
                  }
                </p>
              </div>
              <div className="welcome-actions">
                <button className="welcome-btn primary" onClick={() => { setCreateType('post'); setIsCreating(true); }}>
                  Create Post
                </button>
                <button className="welcome-btn secondary" onClick={() => setShowAddProduct(true)}>
                  Manage Shop
                </button>
              </div>
            </div>

            {/* 24h Activity Strip */}
            <div className="activity-strip" aria-label="Last 24 hours">
              <div className="activity-strip-label">Last 24h</div>
              <div className="activity-strip-items">
                <div className="activity-pill"><span className="activity-num">{realMetrics.newSubscriptions24h}</span><span className="activity-lbl">new subs</span></div>
                <div className="activity-pill"><span className="activity-num">{realMetrics.newUsers24h}</span><span className="activity-lbl">signups</span></div>
                <div className="activity-pill"><span className="activity-num">{realMetrics.newMessages24h}</span><span className="activity-lbl">messages</span></div>
                <div className="activity-pill"><span className="activity-num">{realMetrics.newOffers24h}</span><span className="activity-lbl">offers</span></div>
              </div>
            </div>

            {/* Where signups came from — first-touch UTM attribution.
                The card uses a LIGHT surface so the dashboard's default
                light/white text would be invisible. All inline text
                colors are pinned to dark values so the card is readable
                regardless of the parent dashboard theme. */}
            {attribution && attribution.totalSignups > 0 && (
              <div className="attribution-card" style={{
                /* Longhand so the gradient + screen-blend in
                   AdminContentDashboard.css can layer on top. Using the
                   `background` shorthand would silently reset
                   background-image:none and erase the gradient. */
                backgroundColor: 'var(--light-surface)',
                border: '1px solid var(--light-border, rgba(0,0,0,0.10))',
                borderRadius: 14,
                padding: 18,
                marginTop: 16,
                color: '#0f172a',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
                  <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#0f172a' }}>Where signups came from</h3>
                  <span style={{ fontSize: 12, color: '#475569' }}>last {attribution.windowDays} days · {attribution.totalSignups} signups</span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {attribution.bySource.map(row => {
                    const pct = Math.round((row.count / attribution.totalSignups) * 100);
                    const isReddit = /reddit/i.test(row.source);
                    const label = (row.source && row.source.trim()) || 'unknown';
                    const icon = isReddit ? '🅡' : label === 'direct' ? '🌐' : label === 'unknown' ? '❓' : '🔗';
                    return (
                      <div
                        key={label}
                        style={{
                          padding: '8px 14px',
                          borderRadius: 999,
                          background: isReddit
                            ? 'linear-gradient(135deg, rgba(255, 69, 0, 0.18), rgba(236, 72, 153, 0.18))'
                            : '#e2e8f0',
                          border: `1px solid ${isReddit ? 'rgba(255, 69, 0, 0.45)' : 'rgba(15,23,42,0.08)'}`,
                          fontSize: 13,
                          fontWeight: 600,
                          color: '#0f172a',
                          display: 'inline-flex',
                          gap: 8,
                          alignItems: 'center',
                        }}
                      >
                        <span>{icon}</span>
                        <span style={{ textTransform: 'capitalize' }}>{label}</span>
                        <span style={{ color: '#475569', fontWeight: 500 }}>{row.count} · {pct}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Metrics Grid */}
            <div className="metrics-grid">
              <div className="metric-card">
                <div className="metric-icon">💵</div>
                <div className="metric-label">Total Revenue</div>
                <span className="metric-value">${realMetrics.totalRevenue.toLocaleString()}</span>
                <span className="metric-sub muted">Sub + product lifetime</span>
              </div>
              <div className="metric-card">
                <div className="metric-icon">💎</div>
                <div className="metric-label">Active Subscriptions</div>
                <span className="metric-value">{realMetrics.activeSubscribers.toLocaleString()}</span>
                <span className={`metric-sub ${realMetrics.newSubscriptions24h > 0 ? 'success' : 'muted'}`}>
                  {realMetrics.newSubscriptions24h > 0 ? `↑ ${realMetrics.newSubscriptions24h} in 24h` : 'No change in 24h'}
                </span>
              </div>
              <div className="metric-card">
                <div className="metric-icon">🚀</div>
                <div className="metric-label">Total Likes</div>
                <span className="metric-value">{
                  realMetrics.totalLikes >= 1_000_000
                    ? `${(realMetrics.totalLikes / 1_000_000).toFixed(1)}M`
                    : realMetrics.totalLikes >= 1000
                      ? `${(realMetrics.totalLikes / 1000).toFixed(1)}K`
                      : realMetrics.totalLikes.toLocaleString()
                }</span>
                <span className="metric-sub muted">Across {realMetrics.totalPosts} posts</span>
              </div>
              <div className="metric-card">
                <div className="metric-icon">🛍️</div>
                <div className="metric-label">Products Sold</div>
                <span className="metric-value">{realMetrics.productsSold.toLocaleString()}</span>
                <span className="metric-sub muted">{realMetrics.activeProducts} active listings</span>
              </div>
            </div>

            {/* Payouts & Alerts Row */}
            <div className="metrics-grid payouts-grid">
              <div className="metric-card payout-card">
                <div className="metric-icon">🏦</div>
                <div className="metric-label">Available Balance</div>
                <span className="metric-value">${realMetrics.availableBalance.toLocaleString()}</span>
                <span className="metric-sub muted">Ready to withdraw</span>
              </div>
              <div className="metric-card payout-card">
                <div className="metric-icon">⏳</div>
                <div className="metric-label">Pending Balance</div>
                <span className="metric-value">${realMetrics.pendingBalance.toLocaleString()}</span>
                <span className="metric-sub muted">Clears in ~3 days</span>
              </div>
              <div className="metric-card payout-card">
                <div className="metric-icon">💝</div>
                <div className="metric-label">Tip Revenue</div>
                <span className="metric-value">${realMetrics.tipRevenue.toLocaleString()}</span>
                <span className="metric-sub muted">From post tips</span>
              </div>
              <div className="metric-card payout-card">
                <div className="metric-icon">⚠️</div>
                <div className="metric-label">Low Stock Items</div>
                <span className="metric-value">{realMetrics.lowStockItems}</span>
                <span className={`metric-sub ${realMetrics.lowStockItems > 0 ? 'warn' : 'muted'}`}>
                  {realMetrics.lowStockItems > 0 ? 'Restock soon' : 'All good'}
                </span>
              </div>
            </div>

            {/* Recent Activity Widgets */}
            <div className="widgets-grid">
              {/* Recent Posts Widget */}
              <div className="widget">
                <div className="widget-header">
                  <h3>Recent Posts</h3>
                  <button onClick={() => setActiveSection('posts')}>View All</button>
                </div>
                <div className="widget-content">
                  {regularPosts.slice(0, 3).map(post => (
                    <div key={post.id} className="widget-item" onClick={() => setViewingPost(post)}>
                      {post.mediaUrl && !brokenMedia[post.mediaUrl] && (
                        <img
                          src={post.mediaUrl}
                          alt=""
                          className="widget-thumb"
                          onError={() => markBroken(post.mediaUrl)}
                        />
                      )}
                      <div className="widget-item-info">
                        <p>{post.content?.slice(0, 50) || 'Media post'}...</p>
                        <span>{post.likes} likes • {formatTime(post.timestamp)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recent Messages Widget */}
              <div className="widget">
                <div className="widget-header">
                  <h3>Recent Messages</h3>
                  <button onClick={() => setActiveSection('messages')}>View All</button>
                </div>
                <div className="widget-content">
                  {inboxConversations.slice(0, 3).map(conv => (
                    <div
                      key={conv.partnerId}
                      className={`widget-item ${conv.unreadCount > 0 ? 'unread' : ''}`}
                      onClick={() => { setActiveSection('messages'); setSelectedConversation(conv.partnerId); markConversationRead(conv.partnerId); }}
                    >
                      {conv.partnerAvatar && !brokenMedia[conv.partnerAvatar]
                        ? <img src={conv.partnerAvatar} alt="" className="widget-avatar" onError={() => markBroken(conv.partnerAvatar)} />
                        : <div className="widget-avatar-initials">{(conv.partnerName || '?')[0].toUpperCase()}</div>}
                      <div className="widget-item-info">
                        <p><strong>{conv.partnerName}</strong></p>
                        <span>{conv.lastMessage.content.slice(0, 40)}...</span>
                      </div>
                      {conv.unreadCount > 0 && <span className="unread-dot"></span>}
                    </div>
                  ))}
                </div>
              </div>

              {/* Pending Offers Widget */}
              <div className="widget">
                <div className="widget-header">
                  <h3>Pending Offers</h3>
                  <button onClick={() => setActiveSection('offers')}>View All</button>
                </div>
                <div className="widget-content">
                  {pendingOffers.slice(0, 3).map(({ product, offer }) => (
                    <div key={offer.id} className="widget-item">
                      <img src={product.images[0]} alt="" className="widget-thumb" />
                      <div className="widget-item-info">
                        <p><strong>${offer.amount}</strong> for {product.name}</p>
                        <span>From {offer.username}</span>
                      </div>
                    </div>
                  ))}
                  {pendingOffers.length === 0 && (
                    <p className="widget-empty">No pending offers</p>
                  )}
                </div>
              </div>

              {/* Top Spenders Widget */}
              {realMetrics.recentSubscribers.length > 0 && (
                <div className="widget">
                  <div className="widget-header">
                    <h3>New Subscribers</h3>
                    <button onClick={() => setActiveSection('subscribers')}>View All</button>
                  </div>
                  <div className="widget-content">
                    {realMetrics.recentSubscribers.slice(0, 4).map((s: any, i: number) => (
                      <div key={s.id || i} className="widget-item">
                        {s.avatar && !brokenMedia[s.avatar]
                          ? <img src={s.avatar} alt="" className="widget-avatar" onError={() => markBroken(s.avatar)} />
                          : <div className="widget-avatar-initials">{(s.display_name || s.email || '?')[0].toUpperCase()}</div>}
                        <div className="widget-item-info">
                          <p><strong>{s.display_name || s.email || 'New fan'}</strong></p>
                          <span>{s.plan_type || 'subscriber'}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* POSTS SECTION */}
        {activeSection === 'posts' && (
          <div className="section-container">
            <div className="section-header">
              <h2>Feed Posts</h2>
              <button className="action-btn primary" onClick={() => { setCreateType('post'); setIsCreating(true); }}>
                + Create Post
              </button>
            </div>

            {/* W7.5: comment moderation queue — collapsed by default */}
            <div className="mod-queue">
              <button
                type="button"
                className="mod-queue-toggle"
                onClick={() => {
                  setModerationOpen(v => {
                    if (!v) fetchModerationQueue();
                    return !v;
                  });
                }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                  <path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z"/>
                </svg>
                <span>Moderate comments</span>
                <span className="mod-queue-caret">{moderationOpen ? '▾' : '▸'}</span>
              </button>
              {moderationOpen && (
                <div className="mod-queue-body">
                  {moderationLoading ? (
                    <div className="mod-queue-empty">Loading…</div>
                  ) : moderationComments.length === 0 ? (
                    <div className="mod-queue-empty">No recent comments</div>
                  ) : (
                    <ul className="mod-queue-list">
                      {moderationComments.map(c => (
                        <li key={c.id} className="mod-queue-item">
                          <div className="mod-queue-meta">
                            <strong>{c.display_name || c.username || 'subscriber'}</strong>
                            <span className="mod-queue-time">{new Date(c.created_at).toLocaleString()}</span>
                          </div>
                          <p className="mod-queue-text">{c.content}</p>
                          {c.post_snippet && (
                            <p className="mod-queue-post">on: <em>{c.post_snippet}</em>{c.is_story ? ' (story)' : ''}</p>
                          )}
                          <button
                            type="button"
                            className="mod-queue-delete"
                            onClick={() => deleteCommentById(c.id)}
                          >
                            Delete
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>

            {/* W7.3: post engagement metrics — at-a-glance totals + top
                3 posts so the creator sees what content is performing
                without leaving the dashboard. */}
            {regularPosts.length > 0 && (() => {
              const totalLikes = regularPosts.reduce((s, p) => s + (p.likes || 0), 0);
              const totalComments = regularPosts.reduce((s, p) => s + (p.comments || 0), 0);
              const totalViews = regularPosts.reduce((s, p) => s + ((p as any).views || 0), 0);
              const avgLikes = Math.round(totalLikes / regularPosts.length);
              // Score by engagement: views weighted lightly, likes + comments more.
              const topPosts = [...regularPosts]
                .map(p => ({
                  ...p,
                  _score: (p.likes || 0) + ((p.comments || 0) * 2) + ((((p as any).views || 0) * 0.05)),
                }))
                .sort((a, b) => b._score - a._score)
                .slice(0, 3);
              return (
                <div className="engagement-panel">
                  <div className="engagement-metrics">
                    <div className="eng-metric"><span className="eng-num">{totalLikes.toLocaleString()}</span><span className="eng-lbl">Total likes</span></div>
                    <div className="eng-metric"><span className="eng-num">{totalComments.toLocaleString()}</span><span className="eng-lbl">Comments</span></div>
                    <div className="eng-metric"><span className="eng-num">{totalViews.toLocaleString()}</span><span className="eng-lbl">Views</span></div>
                    <div className="eng-metric"><span className="eng-num">{avgLikes.toLocaleString()}</span><span className="eng-lbl">Avg likes/post</span></div>
                  </div>
                  {topPosts.length > 0 && (
                    <div className="engagement-top">
                      <span className="eng-top-label">🔥 Top performing</span>
                      <ol className="eng-top-list">
                        {topPosts.map(p => (
                          <li key={p.id}>
                            <span className="eng-top-content">{(p.content || '').slice(0, 60) || '(media only)'}</span>
                            <span className="eng-top-stats">
                              {p.likes || 0} ♥ · {p.comments || 0} 💬{(p as any).views ? ` · ${(p as any).views} 👁` : ''}
                            </span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}
                </div>
              );
            })()}

            {regularPosts.length === 0 ? (
              <div className="empty-state">
                <span>📭</span>
                <p>No posts yet</p>
                <button onClick={() => { setCreateType('post'); setIsCreating(true); }}>Create your first post</button>
              </div>
            ) : (
              <div className="posts-grid">
                {regularPosts.map(post => {
                  const broken = !!(post.mediaUrl && brokenMedia[post.mediaUrl]);
                  return (
                  <div key={post.id} className="post-card" onClick={() => setViewingPost(post)}>
                    {post.mediaUrl && (
                      <div className={`post-media${broken ? ' post-media-empty' : ''}`}>
                        {!broken && (
                          <img
                            src={post.mediaUrl}
                            alt=""
                            onError={() => markBroken(post.mediaUrl)}
                          />
                        )}
                        {post.isLocked && <span className="locked-badge">${post.price}</span>}
                      </div>
                    )}
                    <div className="post-info">
                      <p className="post-content">{post.content || 'No caption'}</p>
                      <div className="post-stats">
                        <span>❤️ {post.likes}</span>
                        <span>💰 ${post.tips || 0}</span>
                      </div>
                      <span className="post-time">{formatTime(post.timestamp)}</span>
                    </div>
                    <div className="post-actions" onClick={e => e.stopPropagation()}>
                      <button
                        className="delete-btn"
                        onClick={() => {
                          if (window.confirm(`Delete this post? Subscribers will no longer see "${(post.content || 'untitled').slice(0, 40)}". This cannot be undone.`)) {
                            deletePost(post.id);
                          }
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* STORIES SECTION */}
        {activeSection === 'stories' && (
          <div className="section-container">
            <div className="section-header">
              <h2>Active Stories</h2>
              <button className="action-btn secondary" onClick={() => { setCreateType('story'); setIsCreating(true); }}>
                + Add Story
              </button>
            </div>
            {stories.length === 0 ? (
              <div className="empty-state">
                <span>📷</span>
                <p>No active stories</p>
                <button onClick={() => { setCreateType('story'); setIsCreating(true); }}>Add your first story</button>
              </div>
            ) : (
              <div className="stories-grid">
                {stories.map(story => {
                  const storyBroken = !!(story.mediaUrl && brokenMedia[story.mediaUrl]);
                  return (
                  <div key={story.id} className={`story-card${storyBroken ? ' story-card-empty' : ''}`} onClick={() => setViewingStory(story)}>
                    {story.mediaType === 'video' && story.mediaUrl ? (
                      <VideoPreview src={story.mediaUrl} posterUrl={story.posterUrl} hoverPreview alt="" />
                    ) : storyBroken ? (
                      <div className="story-media-placeholder">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="24" height="24" aria-hidden="true">
                          <rect x="3" y="3" width="18" height="18" rx="2"/>
                          <circle cx="8.5" cy="8.5" r="1.5"/>
                          <path d="M21 15l-5-5L5 21"/>
                        </svg>
                      </div>
                    ) : (
                      <img
                        src={story.mediaUrl}
                        alt=""
                        onError={() => markBroken(story.mediaUrl)}
                      />
                    )}
                    <div className="story-overlay">
                      <span>{formatTime(story.timestamp)}</span>
                      {(() => {
                        const exp = story.expiresAt
                          ? new Date(story.expiresAt).getTime()
                          : new Date(story.timestamp).getTime() + 24 * 60 * 60 * 1000;
                        const msLeft = exp - Date.now();
                        if (msLeft <= 0) {
                          return <span className="story-expiry story-expiry--gone" title="Expired">⏱ expired</span>;
                        }
                        const hrs = Math.floor(msLeft / 3_600_000);
                        const mins = Math.floor((msLeft % 3_600_000) / 60_000);
                        const label = hrs > 0 ? `${hrs}h ${mins}m left` : `${mins}m left`;
                        const isUrgent = msLeft < 60 * 60 * 1000;
                        return (
                          <span
                            className={`story-expiry${isUrgent ? ' story-expiry--urgent' : ''}`}
                            title={`Expires ${new Date(exp).toLocaleString()}`}
                          >⏱ {label}</span>
                        );
                      })()}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm('Delete this story? Subscribers will no longer see it.')) {
                            deletePost(story.id);
                          }
                        }}
                        title="Delete story"
                        aria-label="Delete story"
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
                        </svg>
                      </button>
                    </div>
                    {/* Viewer count badge — click to open viewers modal */}
                    <button
                      type="button"
                      className="story-viewers-badge"
                      onClick={(e) => { e.stopPropagation(); setViewersForStory(story); }}
                      title="See who viewed this story"
                      aria-label={`${storyViewerCounts[story.id] ?? 0} viewers — open list`}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14" aria-hidden="true">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                      <span>{storyViewerCounts[story.id] ?? '–'}</span>
                    </button>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* OFFERS SECTION */}
        {/* ════════════════════════════════════════════════════════════════════
            CONSOLIDATED SHOP WORKSPACE
            Products, Orders, and Offers were previously three separate
            sidebar entries that all dealt with the same commerce surface.
            They now live under one Shop tab with an in-section tab strip
            so the admin can flip between catalog work, fulfillment, and
            negotiation without leaving the Shop context.
            Legacy `setActiveSection('orders'|'products'|'offers')` calls
            from widgets/elsewhere still route here — the inner tab gets
            synced from `activeSection` on each render. */}
        {(activeSection === 'shop' || activeSection === 'products' || activeSection === 'orders' || activeSection === 'offers') && (() => {
          const activeTab: ShopTab = (activeSection === 'orders' || activeSection === 'offers' || activeSection === 'products')
            ? activeSection
            : shopTab;
          const selectTab = (tab: ShopTab) => {
            setShopTab(tab);
            setActiveSection(tab); // keep sidebar highlight + deep-links in sync
          };
          return (
          <div className="section-container shop-section">
            <div className="section-header">
              <div>
                <h2>Shop</h2>
                <p className="section-subtitle">
                  {products.length} {products.length === 1 ? 'product' : 'products'}
                  {' · '}
                  {apiOrders.length} {apiOrders.length === 1 ? 'order' : 'orders'}
                  {(pendingOffers.length + pendingShipmentCount) > 0 && (
                    <> · <strong>
                      {pendingOffers.length > 0 && `${pendingOffers.length} offer${pendingOffers.length === 1 ? '' : 's'}`}
                      {pendingOffers.length > 0 && pendingShipmentCount > 0 && ' + '}
                      {pendingShipmentCount > 0 && `${pendingShipmentCount} to ship`}
                    </strong></>
                  )}
                </p>
              </div>
              <div className="shop-header-actions">
                {activeTab === 'products' && (
                  <button className="action-btn primary" onClick={() => setShowAddProduct(true)}>+ Add Product</button>
                )}
                {activeTab === 'orders' && (
                  <button className="action-btn ghost" onClick={() => refreshOrders()} disabled={ordersLoading}>
                    {ordersLoading ? 'Refreshing…' : 'Refresh'}
                  </button>
                )}
              </div>
            </div>

            {/* Tab strip — matches the inbox All/Unread visual language. */}
            <div className="shop-tabs" role="tablist">
              <button
                role="tab"
                aria-selected={activeTab === 'products'}
                className={`shop-tab ${activeTab === 'products' ? 'active' : ''}`}
                onClick={() => selectTab('products')}
              >
                <span className="shop-tab-icon" aria-hidden="true">🛍️</span>
                Products <span className="shop-tab-count">{products.length}</span>
              </button>
              <button
                role="tab"
                aria-selected={activeTab === 'orders'}
                className={`shop-tab ${activeTab === 'orders' ? 'active' : ''} ${pendingShipmentCount > 0 ? 'has-attention' : ''}`}
                onClick={() => selectTab('orders')}
              >
                <span className="shop-tab-icon" aria-hidden="true">🧾</span>
                Orders <span className="shop-tab-count">{apiOrders.length}</span>
                {pendingShipmentCount > 0 && <span className="shop-tab-pending">{pendingShipmentCount} to ship</span>}
              </button>
              <button
                role="tab"
                aria-selected={activeTab === 'offers'}
                className={`shop-tab ${activeTab === 'offers' ? 'active' : ''} ${pendingOffers.length > 0 ? 'has-attention' : ''}`}
                onClick={() => selectTab('offers')}
              >
                <span className="shop-tab-icon" aria-hidden="true">💰</span>
                Offers
                {pendingOffers.length > 0 && <span className="shop-tab-pending">{pendingOffers.length} pending</span>}
              </button>
            </div>

            {/* ───── OFFERS TAB ───── */}
            {activeTab === 'offers' && (
              <>
            <div className="section-header" style={{ display: 'none' }}>
              <h2>Incoming Offers</h2>
              <span className="count-badge">{pendingOffers.length} pending</span>
            </div>
            {pendingOffers.length === 0 ? (
              <div className="empty-state">
                <span>💬</span>
                <p>No pending offers</p>
                <span className="hint">Offers will appear here when customers make them</span>
              </div>
            ) : (
              <div className="offers-list">
                {pendingOffers.map(({ product, offer }) => (
                  <div key={offer.id} className="offer-card">
                    <img src={product.images[0]} alt={product.name} className="offer-img" />
                    <div className="offer-details">
                      <h4>{product.name}</h4>
                      <div className="offer-meta">
                        <span>From: {offer.username}</span>
                        <OfferTimer expiresAt={new Date(offer.expiresAt)} compact />
                      </div>
                      <div className="offer-amount">${offer.amount}</div>
                      {offer.message && <p className="offer-message">"{offer.message}"</p>}
                    </div>
                    <div className="offer-actions">
                      <button
                        className="accept-btn"
                        disabled={offerSubmitting}
                        onClick={() => setAcceptingOffer({ productId: product.id, offerId: offer.id, offer, product })}
                      >
                        Accept
                      </button>
                      <button
                        className="decline-btn"
                        disabled={offerSubmitting}
                        onClick={async () => {
                          const success = await apiDeclineOffer(offer.id);
                          if (success) {
                            toast.success('Offer declined');
                            declineOffer(product.id, offer.id);
                          } else {
                            toast.error('Failed to decline offer');
                          }
                        }}
                      >
                        Decline
                      </button>
                      <div className="counter-row">
                        <input
                          type="number"
                          placeholder="Counter $"
                          value={counterAmounts[offer.id] || ''}
                          onChange={e => setCounterAmounts(prev => ({ ...prev, [offer.id]: e.target.value }))}
                          disabled={offerSubmitting}
                        />
                        <button
                          onClick={async () => {
                            const amount = parseFloat(counterAmounts[offer.id]);
                            if (amount > 0) {
                              const success = await apiCounterOffer(offer.id, amount);
                              if (success) {
                                toast.success(`Counter offer of $${amount} sent`);
                                counterOffer(product.id, offer.id, amount);
                              } else {
                                toast.error('Failed to send counter offer');
                              }
                              setCounterAmounts(prev => ({ ...prev, [offer.id]: '' }));
                            }
                          }}
                          disabled={!counterAmounts[offer.id] || offerSubmitting}
                        >
                          {offerSubmitting ? '...' : 'Send'}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
              </>
            )}

            {/* ───── PRODUCTS TAB ───── */}
            {activeTab === 'products' && (
              <>
            {products.length === 0 ? (
              <div className="empty-state">
                <span>🛍️</span>
                <p>No products yet</p>
                <button onClick={() => setShowAddProduct(true)}>Add your first product</button>
              </div>
            ) : (
              <div className="products-grid">
                {products.map(product => (
                  <div key={product.id} className="product-card">
                    <img src={product.images[0]} alt={product.name} />
                    <div className="product-info">
                      <h4>{product.name}</h4>
                      <p className="product-category">{product.category}</p>
                      <div className="product-meta">
                        <span className="product-price">${product.salePrice || product.price}</span>
                        <span className={`product-stock ${product.stock <= 10 ? 'low' : ''}`}>
                          {product.stock} in stock
                        </span>
                      </div>
                      <div className="product-sales">
                        <span>{product.sold} sold</span>
                        {product.offers && product.offers.filter(o => o.status === 'pending').length > 0 && (
                          <span className="offers-badge">
                            {product.offers.filter(o => o.status === 'pending').length} offers
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="product-actions">
                      <button className="edit-btn" onClick={() => setEditingProduct(product)}>Edit</button>
                      <button className="delete-btn" onClick={() => {
                        if (window.confirm(`Delete "${product.name}"? This cannot be undone.`)) {
                          deleteProduct(product.id);
                        }
                      }}>Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
              </>
            )}

            {/* ───── ORDERS TAB ───── */}
            {activeTab === 'orders' && (
              <>
            <div className="orders-filter-row">
              {(['all', 'paid', 'shipped', 'delivered', 'refunded'] as OrderFilter[]).map(s => {
                const count = s === 'all'
                  ? apiOrders.length
                  : apiOrders.filter(o => o.status === s || (s === 'paid' && o.status === 'processing')).length;
                return (
                  <button
                    key={s}
                    className={`filter-chip ${orderFilter === s ? 'active' : ''}`}
                    onClick={() => setOrderFilter(s)}
                  >
                    {s}{count > 0 && <span className="chip-count"> · {count}</span>}
                  </button>
                );
              })}
            </div>

            {ordersLoading && apiOrders.length === 0 ? (
              <div className="empty-state">
                <span>⏳</span>
                <p>Loading orders…</p>
              </div>
            ) : filteredOrders.length === 0 ? (
              <div className="empty-state">
                <span>🧾</span>
                <p>{orderFilter === 'all' ? 'No orders yet' : `No ${orderFilter} orders`}</p>
              </div>
            ) : (
              <div className="orders-table">
                {filteredOrders.map(order => (
                  <OrderRow
                    key={order.id}
                    order={order}
                    onMarkShipped={async (tracking) => {
                      const ok = await updateOrderStatus(order.id, 'shipped' as OrderStatus, tracking);
                      if (ok) toast.success('Marked as shipped.');
                      else toast.error('Could not update order.');
                    }}
                    onMarkDelivered={async () => {
                      const ok = await updateOrderStatus(order.id, 'delivered' as OrderStatus);
                      if (ok) toast.success('Marked as delivered.');
                      else toast.error('Could not update order.');
                    }}
                    onRefund={async () => {
                      const ok = await updateOrderStatus(order.id, 'refunded' as OrderStatus);
                      if (ok) toast.success('Marked as refunded (bookkeeping only — PayPal refund must be issued separately).');
                      else toast.error('Could not update order.');
                    }}
                    onOpenDetail={() => setViewingOrder(order)}
                  />
                ))}
              </div>
            )}
              </>
            )}
          </div>
          );
        })()}

        {/* MESSAGES SECTION - Full Inbox */}
        {activeSection === 'messages' && (() => {
          const totalUnread = inboxConversations.reduce((n, c) => n + c.unreadCount, 0);
          const q = conversationQuery.trim().toLowerCase();
          const filtered = inboxConversations.filter(c => {
            if (conversationFilter === 'unread' && c.unreadCount === 0) return false;
            if (q && !c.partnerName.toLowerCase().includes(q) && !c.lastMessage.content.toLowerCase().includes(q)) return false;
            return true;
          });
          return (
          <div className={`section-container messages-section${selectedConversation ? ' messages-section--in-chat' : ''}`}>
            <div className="section-header">
              <div>
                <h2>Messages</h2>
                <p className="section-subtitle">
                  {inboxConversations.length} {inboxConversations.length === 1 ? 'conversation' : 'conversations'}
                  {totalUnread > 0 && <> · <strong>{totalUnread} unread</strong></>}
                </p>
              </div>
            </div>

            {inboxConversations.length === 0 ? (
              <div className="empty-state">
                <span>✉️</span>
                <p>No messages yet</p>
                <span className="hint">Conversations with subscribers will appear here</span>
              </div>
            ) : (
              <div className={`inbox-layout ${selectedConversation ? 'inbox-has-active' : ''}`}>
                {/* Left rail: search + filter pills + conversation list */}
                <div className="inbox-rail">
                  <div className="inbox-search">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <circle cx="11" cy="11" r="7"/>
                      <path d="M21 21l-4.35-4.35"/>
                    </svg>
                    <input
                      type="text"
                      placeholder="Search conversations…"
                      value={conversationQuery}
                      onChange={e => setConversationQuery(e.target.value)}
                    />
                    {conversationQuery && (
                      <button className="inbox-search-clear" onClick={() => setConversationQuery('')} aria-label="Clear">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
                      </button>
                    )}
                  </div>

                  <div className="inbox-tabs">
                    <button
                      className={`inbox-tab ${conversationFilter === 'all' ? 'active' : ''}`}
                      onClick={() => setConversationFilter('all')}
                    >
                      All <span className="inbox-tab-count">{inboxConversations.length}</span>
                    </button>
                    <button
                      className={`inbox-tab ${conversationFilter === 'unread' ? 'active' : ''} ${totalUnread > 0 ? 'has-attention' : ''}`}
                      onClick={() => setConversationFilter('unread')}
                    >
                      Unread <span className="inbox-tab-count">{totalUnread}</span>
                    </button>
                    {/* Quick action — clears every conversation's unread
                        badge in one tap. Only surfaces when there's
                        actually something to mark, so it never sits as
                        dead chrome on a clean inbox. */}
                    {totalUnread > 0 && (
                      <button
                        type="button"
                        className="inbox-mark-all"
                        onClick={markAllConversationsRead}
                        title={`Mark all ${totalUnread} unread as read`}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14" aria-hidden="true">
                          <polyline points="5 12 10 17 20 7"/>
                        </svg>
                        Mark all read
                      </button>
                    )}
                  </div>

                  <div className="conversations-list">
                    {messagesLoading && filtered.length === 0 ? (
                      // First-load skeleton — three shimmering rows so the
                      // inbox doesn't feel empty during the first fetch.
                      <div className="conv-skeleton-list" aria-hidden="true">
                        {[0, 1, 2, 3].map(i => (
                          <div key={i} className="conv-skeleton">
                            <div className="conv-skeleton-avatar" />
                            <div className="conv-skeleton-lines">
                              <div className="conv-skeleton-line conv-skeleton-line--name" />
                              <div className="conv-skeleton-line conv-skeleton-line--preview" />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : filtered.length === 0 ? (
                      <div className="conv-empty">
                        {q ? (
                          <>
                            <div className="conv-empty-icon" aria-hidden="true">
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="36" height="36">
                                <circle cx="11" cy="11" r="7"/>
                                <path d="M21 21l-4.35-4.35"/>
                              </svg>
                            </div>
                            <p>No matches for "{q}"</p>
                            <button type="button" className="conv-empty-action" onClick={() => setConversationQuery('')}>Clear search</button>
                          </>
                        ) : conversationFilter === 'unread' ? (
                          <>
                            <div className="conv-empty-icon" aria-hidden="true">
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="36" height="36">
                                <polyline points="5 12 10 17 20 7"/>
                              </svg>
                            </div>
                            <p>You're all caught up</p>
                            <button type="button" className="conv-empty-action" onClick={() => setConversationFilter('all')}>Show all conversations</button>
                          </>
                        ) : (
                          <>
                            <div className="conv-empty-icon" aria-hidden="true">
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" width="36" height="36">
                                <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
                              </svg>
                            </div>
                            <p>No conversations yet</p>
                            <span className="conv-empty-hint">Subscriber DMs will appear here as they message you.</span>
                          </>
                        )}
                      </div>
                    ) : (() => {
                      // Group by recency bucket. The filtered list is
                      // already sorted newest-first by inboxConversations,
                      // so the bucket order in the output naturally
                      // matches Today → Yesterday → Week → Earlier.
                      const buckets: { key: string; label: string; items: typeof filtered }[] = [];
                      let current: { key: string; label: string; items: typeof filtered } | null = null;
                      for (const conv of filtered) {
                        const b = conversationBucket(conv.lastMessage.timestamp);
                        if (!current || current.key !== b.key) {
                          current = { key: b.key, label: b.label, items: [] };
                          buckets.push(current);
                        }
                        current.items.push(conv);
                      }
                      return buckets.map(bucket => (
                        <React.Fragment key={bucket.key}>
                          <div className="conv-bucket-divider">
                            <span>{bucket.label}</span>
                            <span className="conv-bucket-count">{bucket.items.length}</span>
                          </div>
                          {bucket.items.map(conv => (
                            <button
                              key={conv.partnerId}
                              className={`conversation-item ${selectedConversation === conv.partnerId ? 'active' : ''} ${conv.unreadCount > 0 ? 'unread' : ''}`}
                              onClick={() => { setSelectedConversation(conv.partnerId); markConversationRead(conv.partnerId); }}
                            >
                              <div className="conv-avatar-wrap">
                                {conv.partnerAvatar && !brokenMedia[conv.partnerAvatar]
                                  ? <img src={conv.partnerAvatar} alt="" onError={() => markBroken(conv.partnerAvatar)} />
                                  : <div className="conv-initials">{conv.partnerName?.charAt(0)?.toUpperCase() || '?'}</div>
                                }
                                {(conv.partnerIsOnline ?? isOnline(conv.partnerLastSeen)) && (
                                  <span className="conv-online-dot" title="Online now" />
                                )}
                              </div>
                              <div className="conv-info">
                                <div className="conv-info-top">
                                  <span className="conv-name">
                                    {conv.partnerName}
                                    {(conv.partnerIsOnline ?? isOnline(conv.partnerLastSeen)) ? (
                                      <span className="conv-presence conv-presence-online">Online</span>
                                    ) : conv.partnerLastSeen ? (
                                      <span className="conv-presence">{lastSeenLabel(conv.partnerLastSeen)}</span>
                                    ) : null}
                                  </span>
                                  <span className="conv-time">{formatConvTime(conv.lastMessage.timestamp)}</span>
                                </div>
                                <div className="conv-info-bottom">
                                  <span className="conv-preview">
                                    {(conv.lastMessage.fromUserId === adminUser.id || conv.lastMessage.fromUserId === currentUser.id) && <span className="conv-you">You: </span>}
                                    {conv.lastMessage.content || <em>(no preview)</em>}
                                  </span>
                                  {conv.unreadCount > 0 && <span className="conv-badge">{conv.unreadCount}</span>}
                                </div>
                              </div>
                            </button>
                          ))}
                        </React.Fragment>
                      ));
                    })()}
                  </div>
                </div>

                {/* Right: active conversation OR empty state.
                    On mobile (≤820px), the inbox-rail is hidden via CSS when
                    .inbox-has-active is set, so the chat takes full width.
                    The RealtimeChatPanel's onClose prop is wired to clear
                    selection — used as the "back to inbox" button on mobile. */}
                {selectedConversation && (() => {
                  // Tolerate missing conversation: when admin clicks
                  // "Message" on a never-DM'd subscriber, no row exists
                  // in `conversations` yet, so inboxConversations.find
                  // returns undefined. Fall back to the subscribers list
                  // so the chat panel still mounts and the backend can
                  // lazily create the conversation on first send (see
                  // routes/messages.ts:797 getOrCreateConversation).
                  const conv = inboxConversations.find(c => c.partnerId === selectedConversation);
                  const sub = !conv ? subscribers.find((s: any) => s.id === selectedConversation) : null;
                  const partnerName = conv?.partnerName || sub?.display_name || sub?.username || 'Subscriber';
                  const partnerAvatar = conv?.partnerAvatar || sub?.avatar || '';
                  return (
                    <RealtimeChatPanel
                      partnerId={selectedConversation}
                      partnerName={partnerName}
                      partnerAvatar={partnerAvatar}
                      currentUserId={currentUser.id}
                      isAdmin={true}
                      onClose={() => setSelectedConversation(null)}
                      onMessageSent={refreshConversations}
                    />
                  );
                })()}
                {!selectedConversation && (
                  <div className="inbox-empty-pane">
                    <div className="inbox-empty-illustration">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4">
                        <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
                      </svg>
                    </div>
                    <h3>Pick a conversation</h3>
                    <p>Choose a subscriber from the left to start chatting. Unread messages are sorted to the top.</p>
                  </div>
                )}
              </div>
            )}
          </div>
          );
        })()}
        {/* Reddit Funnel — owned by RedditPanel for separation of concerns;
            the panel handles its own data fetch, secrets banner, tabs. */}
        {activeSection === 'reddit' && <RedditPanel />}

        {/* Subscribers Section */}
        {activeSection === 'subscribers' && (() => {
          // Counts off the currently-fetched set
          const allCount = subscribers.length;
          const activeCount = subscribers.filter(s => (s.subscription_status || (s.is_subscribed ? 'active' : 'pending')) === 'active').length;
          const pendingCount = subscribers.filter(s => (s.subscription_status || (s.is_subscribed ? 'active' : 'pending')) === 'pending').length;
          const cancelledCount = subscribers.filter(s => s.subscription_status === 'cancelled').length;

          // MRR estimate: weight each active plan by its monthly equivalent.
          const planMonthly: Record<string, number> = { monthly: 25, quarterly: 65 / 3, yearly: 100 / 12, lifetime: 0 };
          const computedMRR = subscribers.reduce((sum, s) => {
            const active = (s.subscription_status || (s.is_subscribed ? 'active' : 'pending')) === 'active';
            if (!active) return sum;
            const p = (s.plan_type || 'monthly').toLowerCase();
            return sum + (planMonthly[p] || 25);
          }, 0);
          const mrrDisplay = realMetrics.subscriptionRevenue
            ? realMetrics.subscriptionRevenue / 12
            : computedMRR;

          // Filtered + searched list for the card grid
          const q = subscriberQuery.trim().toLowerCase();
          // W6.2: bucket a last_seen ISO into the same buckets the
          // filter exposes. 'never' covers both null and very-old.
          const seenBucket = (lastSeen: string | null | undefined): 'online' | 'today' | 'week' | 'month' | 'never' => {
            if (!lastSeen) return 'never';
            const ageMin = (Date.now() - new Date(lastSeen).getTime()) / 60_000;
            if (ageMin < 5) return 'online';
            if (ageMin < 60 * 24) return 'today';
            if (ageMin < 60 * 24 * 7) return 'week';
            if (ageMin < 60 * 24 * 30) return 'month';
            return 'never';
          };
          const rows = subscribers.filter(s => {
            if (q && !(s.display_name || '').toLowerCase().includes(q)
                 && !(s.email || '').toLowerCase().includes(q)) return false;
            if (subscriberTierFilter !== 'all') {
              const plan = (s.plan_type || 'monthly').toLowerCase();
              if (plan !== subscriberTierFilter) return false;
            }
            if (subscriberSeenFilter !== 'all') {
              if (seenBucket(s.last_seen) !== subscriberSeenFilter) return false;
            }
            return true;
          });

          // Highlight the matching substring of the search query inside
          // a name or email, so the operator can see WHY a row was kept.
          // Returns either the original string (no match) or a fragment
          // with the matched chunk wrapped in <mark>.
          const highlight = (text: string | null | undefined): React.ReactNode => {
            if (!text) return text || '';
            if (!q) return text;
            const lower = text.toLowerCase();
            const idx = lower.indexOf(q);
            if (idx < 0) return text;
            return (
              <>
                {text.slice(0, idx)}
                <mark className="sub-hl">{text.slice(idx, idx + q.length)}</mark>
                {text.slice(idx + q.length)}
              </>
            );
          };

          // Smart "Joined" date: today/yesterday/weekday/short-date/long-date.
          const formatJoinedDate = (iso: string): string => {
            const date = new Date(iso);
            const now = new Date();
            const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
            const startOfYesterday = startOfToday - 86_400_000;
            const ts = date.getTime();
            if (ts >= startOfToday) return 'Today';
            if (ts >= startOfYesterday) return 'Yesterday';
            if (now.getTime() - ts < 7 * 86_400_000) return date.toLocaleDateString([], { weekday: 'short' });
            if (date.getFullYear() === now.getFullYear()) return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
            return date.toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' });
          };

          return (
          <div className="section-container sub-intel">
            {/* Header — eyebrow + title + subtitle + refresh */}
            <header className="sub-intel-header">
              <div className="sub-intel-titles">
                <span className="sub-intel-eyebrow">Creator Dashboard</span>
                <h2>Subscription Intelligence</h2>
                <p className="sub-intel-subtitle">Live view of subscribers and billing health</p>
              </div>
              <div className="sub-intel-actions">
                <button
                  className="sub-intel-refresh"
                  onClick={() => exportSubscribersCsv(rows)}
                  disabled={subscribersLoading || rows.length === 0}
                  aria-label="Export to CSV"
                  title="Export the current filtered list to CSV"
                  type="button"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                    <polyline points="7 10 12 15 17 10"/>
                    <line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                  <span>Export CSV</span>
                </button>
                <button
                  className="sub-intel-refresh"
                  onClick={() => fetchSubscribers(subscriberFilter)}
                  disabled={subscribersLoading}
                  aria-label="Refresh subscribers"
                  title="Refresh"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={subscribersLoading ? 'spin' : ''}>
                    <path d="M23 4v6h-6"/>
                    <path d="M1 20v-6h6"/>
                    <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/>
                  </svg>
                  <span>Refresh</span>
                </button>
              </div>
            </header>

            {/* Search — 46px, icon left, high-contrast placeholder */}
            <div className="sub-intel-search">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <circle cx="11" cy="11" r="7"/>
                <path d="M21 21l-4.35-4.35"/>
              </svg>
              <input
                type="text"
                placeholder="Search subscribers by name or email"
                value={subscriberQuery}
                onChange={e => setSubscriberQuery(e.target.value)}
              />
              {subscriberQuery && (
                <button className="sub-intel-search-clear" onClick={() => setSubscriberQuery('')} aria-label="Clear">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
                </button>
              )}
            </div>

            {/* KPI strip — MRR is the featured card (first, wider, gradient body) */}
            <div className="sub-intel-kpis">
              <div className="sub-kpi sub-kpi--mrr sub-kpi--featured">
                <span className="sub-kpi-label">Estimated MRR</span>
                <span className="sub-kpi-value">${mrrDisplay.toFixed(0)}</span>
                <span className="sub-kpi-support">Across all active plans</span>
              </div>
              <div className="sub-kpi sub-kpi--active">
                <span className="sub-kpi-label">Active subscribers</span>
                <span className="sub-kpi-value">{activeCount}</span>
                <span className="sub-kpi-support">
                  {realMetrics.newSubscriptions24h > 0
                    ? `+${realMetrics.newSubscriptions24h} in the last 24h`
                    : 'Steady — no new signups today'}
                </span>
              </div>
              <div className="sub-kpi sub-kpi--pending">
                <span className="sub-kpi-label">Pending approvals</span>
                <span className="sub-kpi-value">{pendingCount}</span>
                <span className="sub-kpi-support">
                  {pendingCount > 0 ? 'Needs your review' : 'All caught up'}
                </span>
              </div>
              <div className="sub-kpi sub-kpi--cancelled">
                <span className="sub-kpi-label">Cancelled</span>
                <span className="sub-kpi-value">{cancelledCount}</span>
                <span className="sub-kpi-support">
                  {cancelledCount > 0 ? 'Win-back targets' : 'No churn this period'}
                </span>
              </div>
            </div>

            {/* W6.2: secondary filters — tier + last-seen, combine with status tabs */}
            <div className="sub-intel-filters">
              <label className="sub-intel-filter">
                <span>Tier</span>
                <select
                  value={subscriberTierFilter}
                  onChange={e => setSubscriberTierFilter(e.target.value as any)}
                >
                  <option value="all">All tiers</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="yearly">Yearly</option>
                  <option value="lifetime">Lifetime</option>
                </select>
              </label>
              <label className="sub-intel-filter">
                <span>Last seen</span>
                <select
                  value={subscriberSeenFilter}
                  onChange={e => setSubscriberSeenFilter(e.target.value as any)}
                >
                  <option value="all">Any time</option>
                  <option value="online">Online now</option>
                  <option value="today">Today</option>
                  <option value="week">This week</option>
                  <option value="month">This month</option>
                  <option value="never">Never / 30d+</option>
                </select>
              </label>
              {(subscriberTierFilter !== 'all' || subscriberSeenFilter !== 'all') && (
                <button
                  type="button"
                  className="sub-intel-filter-clear"
                  onClick={() => { setSubscriberTierFilter('all'); setSubscriberSeenFilter('all'); }}
                >
                  Clear filters
                </button>
              )}
            </div>

            {/* Filter tabs — 40px, 4 tabs, counts always visible */}
            <div className="sub-intel-tabs" role="tablist">
              {([
                { id: 'all',       label: 'All',       n: allCount },
                { id: 'active',    label: 'Active',    n: activeCount },
                { id: 'pending',   label: 'Pending',   n: pendingCount },
                { id: 'cancelled', label: 'Cancelled', n: cancelledCount },
              ] as const).map(p => (
                <button
                  key={p.id}
                  role="tab"
                  aria-selected={subscriberFilter === p.id}
                  className={`sub-intel-tab ${subscriberFilter === p.id ? 'active' : ''} ${p.id === 'pending' && p.n > 0 ? 'has-attention' : ''}`}
                  onClick={() => setSubscriberFilter(p.id)}
                >
                  <span className="sub-intel-tab-label">{p.label}</span>
                  <span className="sub-intel-tab-count">{p.n}</span>
                </button>
              ))}
            </div>

            {/* W6.1: bulk-action toolbar. Renders in two modes:
                  - Quick affordance when nothing is selected (just a
                    "Select all visible" button + visible count) so the
                    operator notices bulk actions exist without having
                    to click into every row.
                  - Full toolbar when 1+ selected — approve / deny /
                    export / clear. */}
            {rows.length > 0 && selectedSubIds.size === 0 && (
              <div className="sub-bulk-toolbar sub-bulk-toolbar--idle" role="toolbar">
                <span className="sub-bulk-hint">
                  {rows.length} {rows.length === 1 ? 'subscriber' : 'subscribers'} on this view
                </span>
                <button
                  type="button"
                  className="sub-bulk-btn sub-bulk-btn--ghost"
                  onClick={() => setSelectedSubIds(new Set(rows.map(r => r.id)))}
                >
                  Select all
                </button>
              </div>
            )}
            {selectedSubIds.size > 0 && (() => {
              const allSelected = selectedSubIds.size === rows.length;
              return (
              <div className="sub-bulk-toolbar" role="toolbar">
                <span className="sub-bulk-count">
                  {selectedSubIds.size} of {rows.length} selected
                </span>
                {!allSelected && (
                  <button
                    type="button"
                    className="sub-bulk-btn sub-bulk-btn--ghost"
                    onClick={() => setSelectedSubIds(new Set(rows.map(r => r.id)))}
                  >
                    Select all
                  </button>
                )}
                <button
                  type="button"
                  className="sub-bulk-btn sub-bulk-btn--approve"
                  onClick={() => handleBulkSubscriberAction('approve')}
                  disabled={bulkActionRunning}
                >
                  ✓ Approve
                </button>
                <button
                  type="button"
                  className="sub-bulk-btn sub-bulk-btn--deny"
                  onClick={() => handleBulkSubscriberAction('deny')}
                  disabled={bulkActionRunning}
                >
                  ✕ Deny
                </button>
                <button
                  type="button"
                  className="sub-bulk-btn"
                  onClick={() => exportSubscribersCsv(rows.filter(r => selectedSubIds.has(r.id)))}
                  disabled={bulkActionRunning}
                >
                  Export selected
                </button>
                <button
                  type="button"
                  className="sub-bulk-btn sub-bulk-btn--ghost"
                  onClick={() => setSelectedSubIds(new Set())}
                  disabled={bulkActionRunning}
                >
                  Clear
                </button>
              </div>
              );
            })()}

            {/* Subscriber list — mobile-first cards (replaces table). */}
            {subscribersLoading && rows.length === 0 ? (
              <ul className="sub-card-list sub-card-list--skeleton" aria-hidden="true">
                {[0,1,2,3,4].map(i => (
                  <li key={i} className="sub-card sub-card--skeleton">
                    <span className="sub-skel sub-skel--check" />
                    <span className="sub-skel sub-skel--avatar" />
                    <div className="sub-skel-lines">
                      <span className="sub-skel sub-skel--name" />
                      <span className="sub-skel sub-skel--email" />
                      <span className="sub-skel sub-skel--meta" />
                    </div>
                  </li>
                ))}
              </ul>
            ) : rows.length === 0 ? (
              <div className="sub-empty">
                {q ? (
                  <>
                    <div className="sub-empty-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="36" height="36">
                        <circle cx="11" cy="11" r="7"/>
                        <path d="M21 21l-4.35-4.35"/>
                      </svg>
                    </div>
                    <h3>No subscribers match "{q}"</h3>
                    <p>Try a different name or email, or clear filters to widen the search.</p>
                    <button type="button" className="sub-empty-action" onClick={() => setSubscriberQuery('')}>Clear search</button>
                  </>
                ) : (subscriberTierFilter !== 'all' || subscriberSeenFilter !== 'all' || subscriberFilter !== 'all') ? (
                  <>
                    <div className="sub-empty-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="36" height="36">
                        <line x1="4" y1="6" x2="20" y2="6"/>
                        <line x1="7" y1="12" x2="17" y2="12"/>
                        <line x1="10" y1="18" x2="14" y2="18"/>
                      </svg>
                    </div>
                    <h3>No matches for these filters</h3>
                    <p>Try widening the tier or last-seen filter.</p>
                    <button
                      type="button"
                      className="sub-empty-action"
                      onClick={() => {
                        setSubscriberTierFilter('all');
                        setSubscriberSeenFilter('all');
                        setSubscriberFilter('all');
                      }}
                    >
                      Reset filters
                    </button>
                  </>
                ) : (
                  <>
                    <div className="sub-empty-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" width="36" height="36">
                        <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
                        <circle cx="12" cy="7" r="4"/>
                      </svg>
                    </div>
                    <h3>No subscribers yet</h3>
                    <p>New signups land here in real time. Send a link or push a post to invite first fans.</p>
                  </>
                )}
              </div>
            ) : (
              <ul className="sub-card-list">
                {rows.map((sub: any) => {
                  const isActive = sub.is_subscribed === 1 || sub.is_subscribed === true;
                  const subStatus = sub.subscription_status || (isActive ? 'active' : 'pending');
                  const plan = (sub.plan_type || 'monthly').toLowerCase();
                  const initials = (sub.display_name || sub.email || 'U').trim().charAt(0).toUpperCase();
                  return (
                    <li
                      key={sub.id}
                      className={`sub-card sub-card--${subStatus}${selectedSubIds.has(sub.id) ? ' is-selected' : ''}`}
                      onClick={() => setPreviewSub(sub as SubscriberPreview)}
                    >
                      <label
                        className="sub-card-select"
                        onClick={e => e.stopPropagation()}
                        title="Select for bulk action"
                      >
                        <input
                          type="checkbox"
                          checked={selectedSubIds.has(sub.id)}
                          onChange={e => {
                            setSelectedSubIds(prev => {
                              const next = new Set(prev);
                              if (e.target.checked) next.add(sub.id); else next.delete(sub.id);
                              return next;
                            });
                          }}
                        />
                      </label>
                      <div className="sub-card-identity">
                        {sub.avatar && !brokenMedia[sub.avatar]
                          ? <img src={sub.avatar} alt="" className="sub-card-avatar" onError={() => markBroken(sub.avatar)} />
                          : <div className="sub-card-avatar sub-card-initials">{initials}</div>
                        }
                        <div className="sub-card-text">
                          <div className="sub-card-name-row">
                            <span className="sub-card-name">{sub.display_name ? highlight(sub.display_name) : '—'}</span>
                            <span className={`sub-card-status sub-card-status--${subStatus}`}>
                              <span className="sub-card-status-dot" />
                              {subStatus.charAt(0).toUpperCase() + subStatus.slice(1)}
                            </span>
                          </div>
                          <div className="sub-card-email">{highlight(sub.email)}</div>
                          <div className="sub-card-meta">
                            <span className={`sub-card-plan sub-card-plan--${plan}`}>{plan}</span>
                            <span className="sub-card-dot" aria-hidden="true">·</span>
                            <span>Joined {formatJoinedDate(sub.created_at)}</span>
                            {sub.paid_at && (
                              <>
                                <span className="sub-card-dot" aria-hidden="true">·</span>
                                <span>Paid {new Date(sub.paid_at).toLocaleDateString([], { month: 'short', day: 'numeric' })}</span>
                              </>
                            )}
                            {sub.last_seen && (
                              <>
                                <span className="sub-card-dot" aria-hidden="true">·</span>
                                <span
                                  className={isOnline(sub.last_seen) ? 'sub-card-online' : 'sub-card-lastseen'}
                                  style={isOnline(sub.last_seen)
                                    ? { color: '#22c55e', fontWeight: 600 }
                                    : { opacity: 0.75 }}
                                >
                                  {isOnline(sub.last_seen) ? '● Online now' : lastSeenLabel(sub.last_seen)}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Contextual actions — never show disabled-Approve on active rows */}
                      <div className="sub-card-actions" onClick={e => e.stopPropagation()}>
                        <button
                          className="sub-btn sub-btn--ghost sub-btn--icon"
                          onClick={async () => {
                            try {
                              await navigator.clipboard?.writeText(sub.email || '');
                              toast.success('Email copied');
                            } catch {
                              toast.error('Could not copy email');
                            }
                          }}
                          title="Copy email address"
                          aria-label="Copy email address"
                          type="button"
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                            <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
                          </svg>
                        </button>
                        <button
                          className="sub-btn sub-btn--ghost"
                          onClick={() => setPushEmailRecipient({
                            kind: 'user',
                            userId: sub.id,
                            displayName: sub.display_name,
                            email: sub.email,
                          })}
                          title="Send a custom email to this subscriber"
                        >📧 Push</button>
                        {subStatus === 'pending' && (
                          <>
                            <button className="sub-btn sub-btn--primary" onClick={() => handleSubscriberAction(sub.id, 'approve')}>Approve</button>
                            <button className="sub-btn sub-btn--ghost" onClick={() => handleSubscriberAction(sub.id, 'deny')}>Reject</button>
                          </>
                        )}
                        {subStatus === 'active' && (
                          <button className="sub-btn sub-btn--danger" onClick={() => handleSubscriberAction(sub.id, 'deny')}>Revoke</button>
                        )}
                        {subStatus === 'cancelled' && (
                          <button className="sub-btn sub-btn--primary" onClick={() => handleSubscriberAction(sub.id, 'approve')}>Reactivate</button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          );
        })()}
      </main>

      {/* Post Viewer Modal */}
      {viewingPost && (
        <div className="modal-backdrop" onClick={() => setViewingPost(null)}>
          <div className="post-viewer-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setViewingPost(null)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
            {viewingPost.mediaUrl && (
              <div className={`viewer-media${brokenMedia[viewingPost.mediaUrl] ? ' viewer-media-broken' : ''}`}>
                {brokenMedia[viewingPost.mediaUrl] ? (
                  <div className="viewer-media-placeholder">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="40" height="40" aria-hidden="true">
                      <rect x="3" y="3" width="18" height="18" rx="2"/>
                      <circle cx="8.5" cy="8.5" r="1.5"/>
                      <path d="M21 15l-5-5L5 21"/>
                    </svg>
                    <span>Media unavailable</span>
                  </div>
                ) : viewingPost.mediaType === 'video' ? (
                  <video
                    src={viewingPost.mediaUrl}
                    controls
                    onError={() => markBroken(viewingPost.mediaUrl)}
                  />
                ) : (
                  <img
                    src={viewingPost.mediaUrl}
                    alt=""
                    onError={() => markBroken(viewingPost.mediaUrl)}
                  />
                )}
              </div>
            )}
            <div className="viewer-content">
              <div className="viewer-header">
                <img src={currentUser.avatar || adminUser.avatar} alt="" />
                <div>
                  <span className="viewer-name">{currentUser.displayName || adminUser.displayName}</span>
                  <span className="viewer-time">{viewingPost.timestamp.toLocaleDateString()}</span>
                </div>
              </div>
              <p className="viewer-caption">{viewingPost.content}</p>
              <div className="viewer-stats">
                <span>❤️ {viewingPost.likes} likes</span>
                <span>💰 ${viewingPost.tips || 0} tips</span>
              </div>
              {viewingPost.isLocked && (
                <div className="viewer-premium">
                  <span>Premium Content - ${viewingPost.price}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create Post/Story Modal */}
      {isCreating && (
        <div className="modal-backdrop" onClick={() => setIsCreating(false)}>
          <div className="create-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{createType === 'post' ? 'Create New Post' : 'Add to Story'}</h2>
              <button className="modal-close" onClick={() => setIsCreating(false)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            <div className="modal-body">
              <div className="upload-area" onClick={() => fileInputRef.current?.click()}>
                {mediaUrl ? (
                  <div className="preview">
                    {mediaType === 'video' ? (
                      <video src={mediaUrl} controls />
                    ) : (
                      <img src={mediaUrl} alt="Preview" />
                    )}
                    <button className="remove-media" onClick={e => { e.stopPropagation(); setMediaUrl(''); }} aria-label="Remove media" title="Remove media">
                      ×
                    </button>
                  </div>
                ) : (
                  <div className="upload-placeholder">
                    {uploading ? (
                      <span>Uploading...</span>
                    ) : (
                      <>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                          <circle cx="8.5" cy="8.5" r="1.5"/>
                          <polyline points="21 15 16 10 5 21"/>
                        </svg>
                        <span>Click to upload image or video</span>
                      </>
                    )}
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,video/*"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />
              </div>

              {createType === 'post' && (
                <textarea
                  className="caption-input"
                  placeholder="Write a caption..."
                  value={content}
                  onChange={e => setContent(e.target.value)}
                  rows={4}
                />
              )}

              {createType === 'post' && (
                <div className="premium-toggle">
                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      checked={isLocked}
                      onChange={e => setIsLocked(e.target.checked)}
                    />
                    <span>Premium content (requires payment)</span>
                  </label>
                  {isLocked && (
                    <input
                      type="number"
                      className="price-input"
                      placeholder="Price ($)"
                      value={price}
                      onChange={e => setPrice(e.target.value)}
                    />
                  )}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="cancel-btn" onClick={() => setIsCreating(false)}>Cancel</button>
              <button
                className="publish-btn"
                onClick={handleCreatePost}
                disabled={uploading || (!content.trim() && !mediaUrl)}
              >
                {uploading ? 'Publishing...' : 'Publish'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddProduct && <AddProductModal onClose={() => setShowAddProduct(false)} />}

      {editingProduct && (
        <AddProductModal
          editProduct={editingProduct}
          onClose={() => setEditingProduct(null)}
        />
      )}

      {viewingOrder && (
        <OrderDetailModal order={viewingOrder} onClose={() => setViewingOrder(null)} />
      )}

      {pushEmailRecipient && (
        <PushEmailModal
          recipient={pushEmailRecipient}
          onClose={() => setPushEmailRecipient(null)}
          onSent={(r) => toast.success(`Email sent to ${r.sent} of ${r.total}.`)}
        />
      )}

      {/* Story viewers — admin clicks the eye-badge on a story tile. */}
      {viewersForStory && (
        <StoryViewersModal
          storyId={viewersForStory.id}
          storyCaption={viewersForStory.content}
          onClose={() => setViewersForStory(null)}
          onPushSubscriber={(v) => {
            // Hand off to PushEmail modal — pre-fills the recipient.
            setViewersForStory(null);
            setPushEmailRecipient({
              kind: 'user',
              userId: v.viewer_user_id,
              displayName: v.display_name,
              email: v.email,
            });
          }}
        />
      )}

      {previewSub && (
        <SubscriberPreviewModal
          sub={previewSub}
          onClose={() => setPreviewSub(null)}
          onMessage={(uid) => { setSelectedConversation(uid); setActiveSection('messages'); setPreviewSub(null); markConversationRead(uid); }}
          onApprove={async (uid) => { await handleSubscriberAction(uid, 'approve'); setPreviewSub(null); }}
          onRevoke={async (uid) => { await handleSubscriberAction(uid, 'deny'); setPreviewSub(null); }}
        />
      )}

      {/* Story Viewer — theater overlay (object-fit:contain + blurred matte) */}
      {viewingStory && (() => {
        const idx = stories.findIndex(s => s.id === viewingStory.id);
        const goPrev = () => { if (idx > 0) setViewingStory(stories[idx - 1]); };
        const goNext = () => { if (idx >= 0 && idx < stories.length - 1) setViewingStory(stories[idx + 1]); };
        return (
          <StoryTheater
            story={viewingStory}
            stories={stories}
            index={idx}
            adminUser={adminUser}
            currentUser={currentUser}
            onClose={() => setViewingStory(null)}
            onPrev={goPrev}
            onNext={goNext}
            onDelete={() => { deletePost(viewingStory.id); setViewingStory(null); }}
            formatTime={formatTime}
          />
        );
      })()}

      {/* Accept Offer Confirmation Modal */}
      {acceptingOffer && (
        <div className="modal-backdrop" onClick={() => setAcceptingOffer(null)}>
          <div className="accept-offer-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Accept Offer</h2>
              <button className="modal-close" onClick={() => setAcceptingOffer(null)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="accept-offer-content">
              <div className="offer-summary">
                <img src={acceptingOffer.product.images[0]} alt={acceptingOffer.product.name} />
                <div>
                  <h4>{acceptingOffer.product.name}</h4>
                  <p className="offer-from">From: {acceptingOffer.offer.username}</p>
                  <p className="offer-price">${acceptingOffer.offer.amount}</p>
                </div>
              </div>
              <div className="offer-workflow">
                <h4>What happens next:</h4>
                <ol>
                  <li>
                    <span className="step-icon">📧</span>
                    <span>Email sent to buyer with PayPal payment link</span>
                  </li>
                  <li>
                    <span className="step-icon">⏰</span>
                    <span>Buyer has <strong>12 hours</strong> to complete payment</span>
                  </li>
                  <li>
                    <span className="step-icon">✅</span>
                    <span>Once paid, item is removed from shop</span>
                  </li>
                  <li>
                    <span className="step-icon">🔄</span>
                    <span>If unpaid, goes to next highest bidder or relists</span>
                  </li>
                </ol>
              </div>
              <div className="paypal-preview">
                <span>PayPal Link Preview:</span>
                <code>paypal.me/jalenwardtech/${acceptingOffer.offer.amount}</code>
              </div>
            </div>
            <div className="modal-footer">
              <button className="cancel-btn" onClick={() => setAcceptingOffer(null)}>Cancel</button>
              <button
                className="confirm-accept-btn"
                disabled={offerSubmitting}
                onClick={async () => {
                  try {
                    // Call real API first
                    const result = await apiAcceptOffer(acceptingOffer.offerId);
                    if (result) {
                      toast.success(`Offer accepted! Email sent to ${acceptingOffer.offer.username || 'buyer'} with PayPal link.`);
                      // Also update local context for UI consistency
                      acceptOffer(acceptingOffer.productId, acceptingOffer.offerId);
                    } else {
                      toast.error('Failed to accept offer. Please try again.');
                    }
                  } catch (err) {
                    console.error('Accept offer error:', err);
                    toast.error('Failed to accept offer. Please try again.');
                    // Fallback to local context
                    acceptOffer(acceptingOffer.productId, acceptingOffer.offerId);
                  }
                  setAcceptingOffer(null);
                }}
              >
                {offerSubmitting ? 'Processing...' : 'Accept & Send Email'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminContentDashboard;
